import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import cardFrontImage from "@/assets/images/find_card_front.png";
import cardBackImage from "@/assets/images/find_card_back.svg";
import appProfileImage from "@/assets/images/find_card_app.svg";
import universoCardImage from "@/assets/images/find_card_universo.svg";

import { cn } from "@/utils/utils";

const ITEM_VALUE = "card-number-hint";
const AUTOPLAY_INTERVAL_MS = 4200;
const DRAG_THRESHOLD_PX = 3;

export const CARD_NUMBER_HINT_ID = "card_number_hint";

const SLIDES = [
  {
    image: cardFrontImage,
    title: "card_number_hint.slide_front_title",
    text: "card_number_hint.slide_front_text",
  },
  {
    image: cardBackImage,
    title: "card_number_hint.slide_back_title",
    text: "card_number_hint.slide_back_text",
  },
  {
    image: appProfileImage,
    title: "card_number_hint.slide_app_title",
    text: "card_number_hint.slide_app_text",
  },
  {
    image: universoCardImage,
    title: "card_number_hint.slide_universo_title",
    text: "card_number_hint.slide_universo_text",
  },
];

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export default function CardNumberHint() {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLUListElement>(null);
  const dragRef = useRef<{
    startX: number;
    startScroll: number;
    moved: boolean;
  } | null>(null);

  const [activeSlide, setActiveSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Rato em cima (ou a arrastar) suspende o automático; sai o rato, retoma.
  const isPaused = isHovering || isDragging;

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    setActiveSlide(Math.round(track.scrollLeft / track.clientWidth));
  };

  const goToSlide = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    // Marca já o dot: não depender do evento de scroll, que pode não chegar a tempo.
    setActiveSlide(index);
    track.scrollTo({
      left: index * track.clientWidth,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      const track = trackRef.current;
      // Acordeão fechado ou ainda sem layout: o tick não faz nada.
      if (!track || !track.clientWidth) return;
      // Lê a posição real em vez do estado: evita dessincronizar após um arrasto.
      const current = Math.round(track.scrollLeft / track.clientWidth);
      goToSlide((current + 1) % SLIDES.length);
    }, AUTOPLAY_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [isPaused, goToSlide]);

  const handlePointerDown = (event: React.PointerEvent<HTMLUListElement>) => {
    // Só rato: toque e trackpad já fazem scroll nativo com snap.
    if (event.pointerType !== "mouse") return;
    const track = trackRef.current;
    if (!track) return;

    dragRef.current = {
      startX: event.clientX,
      startScroll: track.scrollLeft,
      moved: false,
    };
    track.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLUListElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    if (!drag || !track) return;

    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < DRAG_THRESHOLD_PX) return;

    drag.moved = true;
    track.scrollLeft = drag.startScroll - delta;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLUListElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;

    dragRef.current = null;
    setIsDragging(false);

    if (track?.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }
    if (!drag?.moved || !track || !track.clientWidth) return;

    // O snap esteve desligado durante o arrasto: fecha no slide mais próximo.
    goToSlide(Math.round(track.scrollLeft / track.clientWidth));
  };

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value={ITEM_VALUE} variant="container" className="my-0 border-[1px] border-[var(--outline-default)] hover:border-[var(--outline-default)] rounded-lg">
        <AccordionTrigger className={cn("text-base h-24",
          "pl-4 pr-1 items-center",
          "bg-[var(--surface-default)]",
          "text-[var(--on-surface-variant-2)]"
        )}>
          {t("card_number_hint.title")}
        </AccordionTrigger>
        <AccordionContent
          id={CARD_NUMBER_HINT_ID}
          aria-label={t("card_number_hint.title")}
        >
          <div
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <ul
              ref={trackRef}
              onScroll={handleScroll}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              aria-roledescription="carousel"
              className={cn(
                "flex list-none m-0 p-0 overflow-x-auto",
                // Esconde a scrollbar sem perder o scroll nativo (swipe táctil).
                "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
                // Snap desligado durante o arrasto, senão luta com o scrollLeft.
                isDragging
                  ? "snap-none cursor-grabbing select-none"
                  : "snap-x snap-mandatory cursor-grab"
              )}
            >
              {SLIDES.map((slide, index) => (
                <li
                  key={slide.title}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={t("card_number_hint.slide_position", {
                    number: index + 1,
                    total: SLIDES.length,
                  })}
                  className="snap-center shrink-0 w-full flex flex-col gap-3"
                >
                  <div className="flex h-[150px] items-center justify-center overflow-hidden rounded-lg bg-[var(--surface-primary-default-variant)]">
                    <img
                      src={slide.image}
                      alt=""
                      draggable={false}
                      className="w-[82%] max-w-[252px] max-h-[130px] object-contain"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5 text-left">
                    <p className="text-[13px] font-bold text-[var(--on-surface-default)]">
                      {t(slide.title)}
                    </p>
                    <p
                      className="text-[13px] leading-[1.45] text-[var(--on-surface-variant)]"
                      dangerouslySetInnerHTML={{ __html: t(slide.text) }}
                    />
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex justify-center pt-2">
              {SLIDES.map((slide, index) => (
                <button
                  key={slide.title}
                  type="button"
                  onClick={() => goToSlide(index)}
                  aria-label={t("card_number_hint.go_to_slide", {
                    number: index + 1,
                    total: SLIDES.length,
                  })}
                  aria-current={index === activeSlide}
                  // Padding só para a área de toque: o ponto visível fica nos 8px do mockup.
                  className="p-2 leading-none"
                >
                  <span
                    className={cn(
                      "block size-2 rounded-full transition-colors",
                      index === activeSlide
                        ? "bg-[var(--surface-primary-fixed-default)]"
                        : "bg-[var(--outline-default-variant)]"
                    )}
                  />
                </button>
              ))}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
